import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  FormGroup,
  Input,
  Container,
  Row,
  Col,
  Button,
  CardHeader,
  UncontrolledTooltip,
} from "reactstrap";
import { useRouteMatch } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import SimpleHeader from "components/Headers/SimpleHeader";
import useApi from "hooks/useApi";
import Spinner from "../../../components/Spinner";
import TextPopup from "components/common/TextModal";
import useAlert from "hooks/useAlert";
import broker from "api/broker";
import usePagination from "hooks/usePagination";
import Pagination from "components/common/Pagination";
import Table from "components/common/CustomTable";
import useSearchPopup from "hooks/useSearchPopup";
import SearchModal from "components/common/SearchModal";
import EditBroker from "./components/EditBroker";
import useAbility from "hooks/useAbility";
import getCulture from "utils/getCulture";
import commonContent from "components/common/content";
import content from "./content";

const Broker = () => {
  const rout = useRouteMatch();
  const culture = getCulture(rout.url);
  const headers = [commonContent.actions[culture], commonContent.name[culture]];
  const columns = ["name"];

  const [data, setData] = useState([]);
  const [spinner, setSpinner] = useState(false);
  const [rowId, setRowId] = useState("");
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const checkAbility = useAbility("Broker");
  const { alert, sweetAlert } = useAlert();

  // use form hook
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm();

  const {
    handleSubmit: editHandleSubmit,
    formState: { errors: editErrors },
    control: editControl,
    setValue: editSetValue,
  } = useForm();

  const { pageNo, setPageNo, docsPerPage, setDocsPerPage } = usePagination("");

  const brokersApi = useApi(broker.brokers);
  const createBrokerApi = useApi(broker.createBroker);
  const getByColumn = useApi(broker.getByColumn);
  const deleteBrokerApi = useApi(broker.deleteBroker);
  const updateBrokerApi = useApi(broker.updateBroker);

  const searchPopup = useSearchPopup(
    getByColumn,
    headers,
    columns,
    data,
    (item) => item
  );

  const createBroker = async (formData) => {
    setSpinner(true);
    const res = await createBrokerApi.request(formData);
    if (res.status === 200) {
      setData((d) => [res.data.data, ...d]);
      sweetAlert("Done");
      reset();
    }
    setSpinner(false);
  };

  const getBrokers = async () => {
    const res = await brokersApi.request(pageNo, docsPerPage);

    if (res.status === 200) {
      setData(res.data.data);
    }
  };

  const updateData = (arr) => {
    setData(arr);
  };

  const handleCancel = () => {
    setRowId("");
    setEditModal(false);
    setDeleteModal(false);
  };

  const handleDelete = (obj) => {
    setRowId(obj.id);
    setDeleteModal(true);
  };

  const deleteRow = async () => {
    setDeleteModal(false);
    setSpinner(true);
    const res = await deleteBrokerApi.request(rowId);
    if (res.status === 200) {
      const newData = data.filter((item) => item.id !== rowId);
      setData(newData);
      sweetAlert("Done");
    }
    setSpinner(false);
  };

  const handleEdit = (obj) => {
    setRowId(obj.id);
    editSetValue("name", obj.name);
    setEditModal(true);
  };

  const editBrokerHandler = async (formData) => {
    setEditModal(false);
    setSpinner(true);
    const res = await updateBrokerApi.request(rowId, formData);

    if (res.status === 200) {
      const newData = [...data];
      const rowIndex = newData.findIndex((item) => item.id === rowId);
      newData[rowIndex] = res.data.data;
      setData(newData);
      sweetAlert("Done");
    }
    setRowId("");
    setSpinner(false);
  };

  const editRenderer = () => checkAbility("edit");

  const deleteRenderer = () => checkAbility("delete");

  useEffect(() => {
    getBrokers();
  }, [pageNo, docsPerPage]);

  if (brokersApi.loading) return <Spinner gate="#29bb64" bg="#fff" />;

  return (
    <div>
      {alert}
      {brokersApi.errorAlert}
      {createBrokerApi.errorAlert}
      {deleteBrokerApi.errorAlert}
      {updateBrokerApi.errorAlert}
      {getByColumn.errorAlert}
      {spinner && <Spinner gate="#29bb64" bg="#fff" opacity />}
      <SimpleHeader parents={[commonContent.broker[culture]]} backBtn />
      <Container className="mt--6" fluid>
        <Card className="card-plain">
          <CardHeader>
            <div className="d-flex justify-content-between">
              <h3 className={`mb-0 text-md-left`}>{commonContent.broker[culture]}</h3>
            </div>
          </CardHeader>
          {checkAbility("create") && (
            <CardBody>
              <form onSubmit={handleSubmit(createBroker)}>
                <Row>
                  <Col md="6" lg="4">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="name">
                        {commonContent.name[culture]}
                      </label>
                      <Controller
                        control={control}
                        name="name"
                        rules={{
                          required: "name field is required",
                        }}
                        render={({
                          field: { ref, onChange, value, ...field },
                        }) => (
                          <Input
                            {...field}
                            id="name"
                            placeholder={commonContent.name[culture]}
                            type="text"
                            className={errors.name && "error"}
                            value={value || ""}
                            onChange={({ target: { value } }) =>
                              onChange(value)
                            }
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Button color="success" type="submit">
                  <span className="btn-inner--icon mr-1">
                    <i className="ni ni-fat-add" />
                  </span>
                  <span>{commonContent.create[culture]}</span>
                </Button>
              </form>
            </CardBody>
          )}
        </Card>
        <Card>
          <Pagination
            dataLength={data.length}
            pageNo={pageNo}
            setPageNo={setPageNo}
            docsPerPage={docsPerPage}
            setDocsPerPage={setDocsPerPage}
          >
            <Table
              pageName="broker"
              headers={headers}
              columns={columns}
              // when the user search using the popup and select a row
              // after then only use the hookData states from searchPopup hook
              data={data}
              setData={setData}
              handleDelete={handleDelete}
              handleEdit={handleEdit}
              editRenderer={editRenderer}
              deleteRenderer={deleteRenderer}
              onHeaderClick={searchPopup.handleHeaderClicked}
            >
              <span fun="handleEdit" className="me-1" condition="editRenderer">
                <div id="edit1" className="table-action cursor-pointer">
                  <i className="fas fa-pencil-alt fa-lg hover-info"></i>
                </div>
                <UncontrolledTooltip delay={0} target="edit1">
                  Edit
                </UncontrolledTooltip>
              </span>
              <span
                className="me-1"
                fun="handleDelete"
                condition="deleteRenderer"
              >
                <div id="delete" className="table-action cursor-pointer">
                  <i className="fas fa-trash hover-danger fa-lg" />
                </div>
                <UncontrolledTooltip delay={0} target="delete">
                  Delete
                </UncontrolledTooltip>
              </span>
            </Table>
          </Pagination>
        </Card>
      </Container>
      <TextPopup
        modal={editModal}
        text={commonContent.save[culture]}
        handleCancel={handleCancel}
        fn={editHandleSubmit(editBrokerHandler)}
        color="primary"
      >
        <Container>
          <EditBroker control={editControl} errors={editErrors} />
        </Container>
      </TextPopup>
      <TextPopup
        modal={deleteModal}
        text={commonContent.yes[culture]}
        handleCancel={handleCancel}
        fn={deleteRow}
        color="danger"
      >
        <Container>
          <h2>{content.deletePopup[culture]}</h2>
        </Container>
      </TextPopup>
      {searchPopup.openPopup && (
        <SearchModal
          data={data}
          setData={setData}
          updateData={updateData}
          searchPopup={searchPopup}
          editResponse={(item) => item}
          // popupSearchCtx={paginationCtx.popupSearch}
          extraField={{ header: "name", column: "name" }}
        />
      )}
    </div>
  );
};

export default Broker;
